import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { BanknotesIcon, ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import Portal from '../../components/Portal';

const api = (path) => `${process.env.REACT_APP_API_URL}${path}`;

const OVERLAY_CLASS =
  'fixed top-0 left-0 z-[55] flex h-[100dvh] w-screen items-center justify-center bg-black/50 p-4';

function formatDateFr(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR');
}

export default function SuiviCheques() {
  const currentYear = new Date().getFullYear();
  const [annee, setAnnee] = useState(currentYear);
  const [statut, setStatut] = useState('non_encaisse');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cheques, setCheques] = useState([]);
  const [confirming, setConfirming] = useState('');
  const [chequeAConfirmer, setChequeAConfirmer] = useState(null);

  const anneesOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 5; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  const fetchCheques = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        api(`/miellerie/cheques?annee=${annee}&statut=${statut}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCheques(Array.isArray(data?.cheques) ? data.cheques : []);
    } catch (err) {
      setCheques([]);
      setError(err.response?.data?.message || 'Erreur lors du chargement des chèques');
    } finally {
      setLoading(false);
    }
  }, [annee, statut]);

  useEffect(() => {
    fetchCheques();
  }, [fetchCheques]);

  const confirmer = async () => {
    const cheque = chequeAConfirmer;
    if (!cheque) return;
    setConfirming(cheque.cle);
    try {
      const token = localStorage.getItem('token');
      const body = cheque.transactionIds?.[0]
        ? { transactionId: cheque.transactionIds[0] }
        : { serviceId: cheque.serviceId };
      await axios.post(api('/miellerie/cheques/encaisser'), body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Chèque marqué encaissé');
      setChequeAConfirmer(null);
      await fetchCheques();
    } catch (err) {
      toast.error(err.response?.data?.message || "Impossible de confirmer l'encaissement");
    } finally {
      setConfirming('');
    }
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm flex-1">{error}</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Chèques</h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Un paiement par chèque est non encaissé tant qu&apos;un administrateur ne confirme pas
            son encaissement.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={statut}
            onChange={(e) => setStatut(e.target.value)}
            className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm"
          >
            <option value="non_encaisse">Non encaissés</option>
            <option value="encaisse">Encaissés</option>
            <option value="tous">Tous</option>
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-slate-600">
            Année
            <select
              value={annee}
              onChange={(e) => setAnnee(parseInt(e.target.value, 10))}
              className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm"
            >
              {anneesOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600" />
        </div>
      ) : cheques.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-10 text-center">
          <BanknotesIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Aucun chèque pour cette sélection</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usager</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden md:table-cell">Détail</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase hidden sm:table-cell">Nº</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {cheques.map((cheque) => {
                const nom = cheque.usager
                  ? `${cheque.usager.prenom || ''} ${cheque.usager.nom || ''}`.trim()
                  : '—';
                const encaisse = cheque.chequeStatut === 'encaisse';
                return (
                  <tr key={cheque.cle} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-slate-900 whitespace-nowrap">
                      {formatDateFr(cheque.date)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">{nom || '—'}</td>
                    <td className="px-4 py-3 text-sm text-slate-600 hidden md:table-cell">
                      {(cheque.descriptions || []).filter(Boolean).join(' · ')}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600 hidden sm:table-cell">
                      {cheque.reference || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-right tabular-nums">
                      {Number(cheque.montant || 0).toFixed(2)} €
                    </td>
                    <td className="px-4 py-3 text-center">
                      {encaisse ? (
                        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Encaissé
                          {cheque.chequeEncaisseLe ? ` le ${formatDateFr(cheque.chequeEncaisseLe)}` : ''}
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setChequeAConfirmer(cheque)}
                          disabled={Boolean(confirming)}
                          className="inline-flex items-center text-xs font-semibold text-honey-800 bg-honey-50 hover:bg-honey-100 border border-honey-200 px-2 py-1 rounded-md disabled:opacity-50"
                        >
                          Marquer encaissé
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {chequeAConfirmer && (
        <Portal>
          <div
            className={OVERLAY_CLASS}
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget && !confirming) setChequeAConfirmer(null);
            }}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-md w-full"
              role="dialog"
              aria-labelledby="confirmer-cheque-title"
              aria-modal="true"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
                <h3 id="confirmer-cheque-title" className="text-base font-semibold text-slate-900">
                  Marquer le chèque encaissé
                </h3>
                <button
                  type="button"
                  onClick={() => setChequeAConfirmer(null)}
                  disabled={Boolean(confirming)}
                  className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50 shrink-0"
                  aria-label="Fermer"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-3 text-sm text-slate-700">
                <p>Confirmer l&apos;encaissement de ce chèque ?</p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 space-y-1">
                  <p className="font-medium text-slate-900">
                    {chequeAConfirmer.usager
                      ? `${chequeAConfirmer.usager.prenom || ''} ${chequeAConfirmer.usager.nom || ''}`.trim() || '—'
                      : '—'}
                  </p>
                  <p className="tabular-nums">
                    {Number(chequeAConfirmer.montant || 0).toFixed(2)} €
                    {chequeAConfirmer.reference ? ` · n° ${chequeAConfirmer.reference}` : ''}
                  </p>
                  <p className="text-slate-500">{formatDateFr(chequeAConfirmer.date)}</p>
                </div>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setChequeAConfirmer(null)}
                  disabled={Boolean(confirming)}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={confirmer}
                  disabled={Boolean(confirming)}
                  className="px-4 py-2 bg-honey-600 hover:bg-honey-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg"
                >
                  {confirming ? 'Enregistrement…' : 'Confirmer l’encaissement'}
                </button>
              </div>
            </div>
          </div>
        </Portal>
      )}
    </div>
  );
}
